import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldCheck, Pencil, Check } from "lucide-react";
import { api, formatError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BottomNav } from "@/components/BottomNav";

const field = "w-full bg-[#F5F8F6] rounded-[14px] px-4 py-3 text-[#122117] placeholder-[#6B8375] focus:outline-none focus:ring-2 focus:ring-[#0B7A3B] border-none";

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    postcode: user?.postcode || "",
    delivery_zone: user?.delivery_zone || "",
  });
  const [saving, setSaving] = useState(false);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      setEditing(false);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F5F8F6] pb-24">
      <div className="bg-gradient-to-br from-[#095F2E] to-[#0B7A3B] px-6 pt-14 pb-12 rounded-b-[32px]">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-extrabold" data-testid="profile-avatar">
            {initials}
          </div>
          <div>
            <h1 className="text-white text-xl font-extrabold">{user?.name}</h1>
            <p className="text-white/80 text-sm">{user?.email}</p>
            {user?.auth_provider === "google" && (
              <span className="inline-block mt-1 text-[10px] bg-white/20 text-white rounded-full px-2 py-0.5">Google account</span>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 -mt-6 space-y-4">
        <LoyaltyCard user={user} />
        <div className="bg-white rounded-[20px] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-extrabold text-[#122117]">Delivery details</h2>
            {!editing ? (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm font-bold text-[#0B7A3B]" data-testid="edit-profile">
                <Pencil size={14} /> Edit
              </button>
            ) : (
              <button onClick={save} disabled={saving} className="flex items-center gap-1 text-sm font-bold text-[#0B7A3B]" data-testid="save-profile">
                <Check size={16} /> {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>

          {editing ? (
            <div className="space-y-3">
              <input className={field} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="pf-name" />
              <input className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="pf-phone" />
              <input className={field} placeholder="Address" value={form.address} onChange={set("address")} data-testid="pf-address" />
              <div className="grid grid-cols-2 gap-3">
                <input className={field} placeholder="Postcode" value={form.postcode} onChange={set("postcode")} data-testid="pf-postcode" />
                <input className={field} placeholder="Zone" value={form.delivery_zone} onChange={set("delivery_zone")} data-testid="pf-zone" />
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm" data-testid="profile-details">
              <Detail label="Phone" value={user?.phone || "—"} />
              <Detail label="Address" value={user?.address || "Not set"} />
              <Detail label="Postcode" value={user?.postcode || "Not set"} />
              <Detail label="Zone" value={user?.delivery_zone || "Not set"} />
            </div>
          )}
        </div>

        <button onClick={() => navigate("/admin")} className="w-full flex items-center justify-center gap-2 bg-white text-[#122117] font-bold rounded-[16px] py-3.5 active:scale-95 transition-transform" data-testid="admin-link">
          <ShieldCheck size={18} className="text-[#0B7A3B]" /> Admin dashboard
        </button>

        <button onClick={doLogout} className="w-full flex items-center justify-center gap-2 text-[#C0392B] font-bold rounded-[16px] py-3.5 active:scale-95 transition-transform" data-testid="logout-btn">
          <LogOut size={18} /> Log out
        </button>
      </div>
      <BottomNav />
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#6B8375]">{label}</span>
      <span className="font-semibold text-[#122117] text-right">{value}</span>
    </div>
  );
}

function LoyaltyCard({ user }) {
  const threshold = user?.loyalty_threshold || 250;
  const spend = user?.lifetime_spend || 0;
  const reward = !!user?.reward_available;
  const toNext = user?.loyalty_to_next ?? threshold;
  const pct = Math.min(100, ((threshold - toNext) / threshold) * 100);
  return (
    <div className="rounded-[20px] p-4 bg-gradient-to-br from-[#0B7A3B] to-[#095F2E] text-white" data-testid="loyalty-card">
      <div className="flex items-center justify-between">
        <h2 className="font-extrabold">Loyalty rewards</h2>
        <span className="text-lg leading-none">&#127873;</span>
      </div>
      <p className="text-white/85 text-sm mt-1">Lifetime spend: <span className="font-bold">{money(spend)}</span></p>
      {reward ? (
        <p className="mt-2 font-bold text-sm bg-white/15 rounded-full px-3 py-1.5 inline-block" data-testid="loyalty-reward-ready">
          Reward ready — service charge waived on your next order!
        </p>
      ) : (
        <>
          <div className="h-2.5 rounded-full bg-white/25 overflow-hidden mt-3">
            <div className="h-full rounded-full bg-white transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-white/85 text-xs mt-1.5">Spend {money(toNext)} more to unlock a free service charge.</p>
        </>
      )}
    </div>
  );
}
